import { Box, Button, Grid, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import useTranslate, { TTranslate } from 'Hooks/useTranslate';
import { Form, SubmitButton } from 'Components/Shared';
import useFormValidation from 'Hooks/useFormValidation';
import { Imgs } from 'Assets/Img';
import { TUsersCreation } from './FormUsersCreation.container';
import TextInputEmails from 'Components/Shared/Form/Fields/TextInputEmails';
import Selector from 'Components/Shared/Form/Fields/Selector';
import { useStyles } from './FormUsersCreation.styles';

type Props = {
  onCancel(): void;
  onSubmit(values: TUsersCreation): void;
  loading?: boolean;
  languages: ILanguage[];
  heading: Parameters<TTranslate>[0];
  subheading: Parameters<TTranslate>[0];
  initialAccount: string;
  submitDisabled?: boolean;
  children: ReactNode;
};

// Shared layout for the account and organization guest invitation forms; the identity
// section (account selector or organization name) is provided by the caller as children.
const FormUsersCreationLayout = ({
  onCancel,
  onSubmit,
  loading,
  languages,
  heading,
  subheading,
  initialAccount,
  submitDisabled,
  children
}: Props) => {
  const ts = useTranslate();
  const intl = useIntl();
  const locale = (
    languages.find((lang: ILanguage) => lang.code.includes(intl.locale)) ??
    languages[0]
  )?.code;

  const { usersCreation } = useFormValidation();
  const classes = useStyles();

  const initValues: TUsersCreation = {
    emails: [],
    account: initialAccount,
    language: locale
  };

  return (
    <Form<TUsersCreation>
      initialValues={initValues}
      onSubmit={onSubmit}
      validationSchema={usersCreation}
    >
      <Box className={classes.container}>
        <Box className={classes.content} pb={3}>
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            alignItems="flex-start"
          >
            <Grid
              item
              container
              justifyContent="center"
              xs={12}
              alignItems="center"
            >
              <Grid item container justifyContent="center" xs={12} md={3}>
                <img
                  width={150}
                  src={Imgs.wizardIllustration.src}
                  alt={Imgs.wizardIllustration.alt}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box pt={1}>
                  <Typography variant="h5" paragraph align="center">
                    {ts(heading)}
                  </Typography>
                  <Typography variant="body2" align="center">
                    {ts(subheading)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box mt={3}>{children}</Box>
              <Box my={3}>
                <Selector
                  name="language"
                  options={languages.map(l => ({
                    value: l.code,
                    label: l.label
                  }))}
                  label="Language"
                  size="medium"
                  variant="outlined"
                />
              </Box>
              <TextInputEmails
                name="emails"
                size="medium"
                multiline
                helperText={ts(
                  'component.modals.createUser.labels.emailsHelperText'
                )}
                label={ts('component.modals.createUser.labels.emails')}
              />
            </Grid>
          </Grid>
        </Box>
        <Grid container justifyContent="center" spacing={1}>
          <Grid item>
            <Button onClick={onCancel} color="inherit">
              {ts('common.action.cancel')}
            </Button>
          </Grid>
          <Grid item>
            <SubmitButton
              title={ts('component.modals.createUser.submit')}
              loading={loading}
              disabled={submitDisabled}
            />
          </Grid>
        </Grid>
      </Box>
    </Form>
  );
};

export default FormUsersCreationLayout;
